import React from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { Button } from '@/components/ui/button';
import { Gamepad2, Sword, Shield, Zap } from 'lucide-react';

export const HomePage: React.FC = () => {
  const { user } = useAuthStore();

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/10 to-background">
      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16 text-center">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-center mb-8">
            <Gamepad2 className="h-16 w-16 text-primary" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
            Tower Wars Evolution
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            A comprehensive Tower Defense game with 15 unique towers, 100 levels of progression,
            and a deep research tree system.
          </p>

          {user ? (
            <div className="space-y-4">
              <Button size="lg" asChild>
                <Link to="/game">
                  <Gamepad2 className="mr-2 h-5 w-5" />
                  Play Now
                </Link>
              </Button>
              <div className="text-sm text-muted-foreground">
                Welcome back, <span className="font-medium">{user.username}</span>!
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild>
                <Link to="/register">
                  Play Now
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/login">
                  Login
                </Link>
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-center text-foreground mb-12">
          Game Features
        </h2>
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div className="text-center p-6 rounded-lg bg-card border border-border">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-primary/10 rounded-full">
                <Sword className="h-8 w-8 text-primary" />
              </div>
            </div>
            <h3 className="text-xl font-semibold mb-3">15 Unique Towers</h3>
            <p className="text-muted-foreground">
              Each with special abilities and upgrade paths. From archers to plasma towers,
              master every type of defense.
            </p>
          </div>

          <div className="text-center p-6 rounded-lg bg-card border border-border">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-primary/10 rounded-full">
                <Shield className="h-8 w-8 text-primary" />
              </div>
            </div>
            <h3 className="text-xl font-semibold mb-3">100-Level Campaign</h3>
            <p className="text-muted-foreground">
              Progressive difficulty with boss encounters every 10 levels.
              Test your strategic thinking across challenging maps.
            </p>
          </div>

          <div className="text-center p-6 rounded-lg bg-card border border-border">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-primary/10 rounded-full">
                <Zap className="h-8 w-8 text-primary" />
              </div>
            </div>
            <h3 className="text-xl font-semibold mb-3">Research Tree</h3>
            <p className="text-muted-foreground">
              Deep progression system with time-based technology unlocking.
              Plan your research strategy to unlock powerful towers.
            </p>
          </div>
        </div>
      </section>

      {/* Game Modes Section */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-center text-foreground mb-12">
          Game Modes
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          <div className="p-6 rounded-lg bg-card border border-border">
            <h3 className="text-xl font-semibold mb-3 text-primary">
              Defense Mode
            </h3>
            <p className="text-muted-foreground mb-4">
              Classic tower defense gameplay with 100 challenging levels.
              Perfect for strategic planning and tower optimization.
            </p>
            <div className="text-sm text-green-600 font-medium">Available Now</div>
          </div>

          <div className="p-6 rounded-lg bg-card border border-border opacity-60">
            <h3 className="text-xl font-semibold mb-3">
              Tower Wars Mode
            </h3>
            <p className="text-muted-foreground mb-4">
              Competitive 2-player battles with income-based troop sending.
              Coming soon in future updates.
            </p>
            <div className="text-sm text-yellow-600 font-medium">Coming Soon</div>
          </div>

          <div className="p-6 rounded-lg bg-card border border-border opacity-60">
            <h3 className="text-xl font-semibold mb-3">
              Tutorial Mode
            </h3>
            <p className="text-muted-foreground mb-4">
              Learn game mechanics with guided tutorials and practice scenarios.
              Perfect for new players.
            </p>
            <div className="text-sm text-yellow-600 font-medium">Coming Soon</div>
          </div>
        </div>
      </section>
    </div>
  );
};